import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Button, Grid, Checkbox, CheckboxProps, Modal, Dimmer, Loader, Icon, Form } from "semantic-ui-react";
import { ReactSVG } from "react-svg";
import { cloneDeep } from "lodash";
import Swal from 'sweetalert2';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { TagViewModel } from '../../types/TagDTO';
import DataGrid, { Column } from '../Common/SharedComponents/DataGrid';
import { DynamicSearchResponse, FilterData } from '../../types/filterDataGridDTO';
import DocumentAdd from "../../assets/images/document-add.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import AdminSidebar from '../Common/SharedComponents/AdminSidebar';
import AppContext, { token_config } from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

const Tags: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo, themeVersion } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_TAGS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const [responseDataModel, setResponseDataModel] = useState<DynamicSearchResponse<TagViewModel> | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterData> | null>(null);

  const [mergeIds, setMergeIds] = useState<number[]>([]);
  const [mergeRecords, setMergeRecords] = useState<any>([]);
  const [newTagName, setNewTagName] = useState<string>('');
  const [totalCaseCount, setTotalCaseCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState<boolean>(false);
  const [wardResponse, setWardResponse] = useState<any>(null);

  const columns: Column<TagViewModel>[] = [
    { field: 'name', header: "Tags", sortable: true, headerStyle: { textAlign: 'left', paddingLeft: 10, width: '50%' }, style: { textAlign: 'left', fontSize: '16px' }, accessor: (row: TagViewModel) => row.name ? <Link to={`/tag/${row.tagId}`}>{row.name}</Link> : null },
    { field: 'caseCount', header: 'Cases', sortable: true, className: "wrap-text", headerStyle: { textAlign: 'left', width: '20%' }, accessor: (row: TagViewModel) => row.caseCount ? row.caseCount : 0 },
    { field: 'office', header: 'Office', sortable: true, className: "wrap-text", style: { width: '30%' }, accessor: (row: TagViewModel) => row.office },
    {
      field: 'merge', header: 'Select', sortable: false, headerStyle: { textAlign: 'center', width: '10%' },
      accessor: (row: TagViewModel) => {
        return (
          <Checkbox
            checked={mergeIds.includes(row.tagId) ? true : false}
            onChange={(e, data) => handleMergeCheckboxChange(e, data, row)}
            style={{
              margin: '1rem 1rem 0rem 1rem',
              fontSize: '1.6rem'
            }}
          />
        );
      }
    },
  ];

  if (userInfo?.IsAllOffices !== "True") {
    columns.splice(2, 1);
  }

  useEffect(() => {
    document.title = 'Tags - Adjure';
  }, []);

  const handleMergeCheckboxChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, row: TagViewModel) => {
    let tmpTotalCaseCount = cloneDeep(totalCaseCount);
    const tmpMergeIds = cloneDeep(mergeIds);
    const tmpMergeRecords = cloneDeep(mergeRecords);
    if (tmpMergeRecords.length > 0 && _.findIndex(tmpMergeRecords, { office: row.office }) === -1) {
      alert('Merge tags allowed within an office');
    } else {
      if (data.checked) {
        tmpMergeIds.push(row.tagId);
        tmpMergeRecords.push(row);
        tmpTotalCaseCount += row.caseCount;
      } else {
        const mergeIdIndex = tmpMergeIds.findIndex((mergeId: number) => mergeId === row.tagId);
        tmpMergeIds.splice(mergeIdIndex, 1);
        const mergeRecordIndex = _.findIndex(mergeRecords, { tagId: row.tagId });
        tmpMergeRecords.splice(mergeRecordIndex, 1);
        tmpTotalCaseCount -= row.caseCount;
      }
      if (responseDataModel && tmpMergeIds.length <= responseDataModel.maxMergeRecords) {
        setMergeIds(tmpMergeIds);
        setTotalCaseCount(tmpTotalCaseCount);
        setMergeRecords(tmpMergeRecords);
      } else {
        alert('Reached maximum allowed tags to merge');
      }
    }
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, tagName: string) => {
    setNewTagName(tagName);
  };

  const closeModal = () => {
    setMergeIds([]);
    setNewTagName('');
    setMergeRecords([]);
    setTotalCaseCount(0);
    setIsMergeModalOpen(false);
  }

  const handleMergeTags = async () => {
    if (newTagName) {
      let payload = {
        mergeTagIds: mergeIds,
        newTagName,
        officeId: mergeRecords[0].officeId
      };
      Swal.fire({
        title: 'Are you sure you want to merge?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Merge',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            axiosInstance.post('/AdminSetting/MergeTags', payload).then(({ data, status }) => {
              if (status === 200) {
                setIsLoading(false);
                Swal.fire({
                  title: 'Tags merged successfully',
                  icon: 'success',
                  html: `<div class="form-custom" style="text-align: left">
                        <p>All ${totalCaseCount} cases are now associated with ${newTagName}.</p>
                        </div>`,
                  confirmButtonText: 'Ok',
                  showConfirmButton: true,
                  showDenyButton: false,
                  allowOutsideClick: false,
                  allowEscapeKey: false
                }).then((result: any) => {
                  if (result.isConfirmed) {
                    window.location.reload();
                  }
                });
              }
            })
              .catch((error) => {
                setIsLoading(false);
                console.log(error);
              });
          } catch (error: any) {
            setIsLoading(false);
            console.log(error);
          }
        }
      });
    } else {
      alert('Please select any one record');
    }
  }

  const handleDeleteTags = () => {
    let isAllowToDelete = true;
    mergeRecords.map((mergeRecord: any) => {
      if (mergeRecord.caseCount > 0) {
        isAllowToDelete = false;
      }
      return true;
    });
    if (isAllowToDelete) {
      Swal.fire({
        title: 'Are you sure you want to delete?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      }).then((result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            axiosInstance.post(`/AdminSetting/DeleteTags/${mergeIds}`, null).then(async ({ data, status }) => {
              if (status === 200) {
                toast.success('Tags deleted successfully');
                const postParameter = { ...responseDataModel };
                const response = await axiosInstance.post(`/AdminSetting/TagDataGrid`, postParameter);
                setResponseDataModel(response.data);
                setWardResponse(response);
                setIsLoading(false);
              }
            })
              .catch((error) => {
                setIsLoading(false);
                console.log(error);
              });
          } catch (error: any) {
            setIsLoading(false);
            console.log(error);
          }
        }
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Ope!',
        text: 'The selected tag(s) are linked with one or more cases and cannot be deleted',
        confirmButtonText: 'Ok',
        showConfirmButton: true,
        showDenyButton: false
      });
    }
  }

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Tags</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          {userInfo?.IsAllOffices !== "True" && (
            <Button color="violet" onClick={() => navigate('/tag')}>
              <Button.Content visible style={{ whiteSpace: 'nowrap' }}>
                Add Tag
              </Button.Content>
              <Button.Content visible>
                <ReactSVG src={DocumentAdd} />
              </Button.Content>
            </Button>
          )}
          <SwitchTheme />
        </div>
      </div>
      <div className="section__body">
        <Grid padded>
          <Grid.Row>
            <Grid.Column mobile={16} tablet={3} computer={3}>
              <AdminSidebar />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={13} computer={13}>
              {mergeIds.length > 0 && (
                <>
                  <div className="section__actions" style={{ float: 'right', marginTop: '4px', position: 'absolute', right: '10px' }}>
                    {mergeIds.length > 1 && (
                      <Button className="btn-bright" style={{ height: '40px' }} onClick={() => setIsMergeModalOpen(true)}>
                        <Button.Content visible>
                          <Icon name="i cursor" />
                        </Button.Content>
                        <Button.Content visible>
                          Merge
                        </Button.Content>
                      </Button>
                    )}
                    <Button className="btn-bright" style={{ height: '40px' }} onClick={() => handleDeleteTags()}>
                      <Button.Content visible>
                        <Icon name="trash" />
                      </Button.Content>
                      <Button.Content visible>
                        Delete
                      </Button.Content>
                    </Button>
                  </div>
                  <Modal
                    onClose={closeModal}
                    open={isMergeModalOpen}
                    className={themeVersion === 'black' ? 'dark-theme' : ''}
                  >
                    <Modal.Header>
                      <span>Merge Tags</span>
                      <span><Icon name="close" style={{ float: "right", cursor: "pointer" }} onClick={closeModal} /></span>
                    </Modal.Header>
                    <Modal.Content>
                      <Dimmer.Dimmable as="div" dimmed={isLoading}>
                        <Dimmer active={isLoading} inverted={true}>
                          <Loader>Loading</Loader>
                        </Dimmer>
                        <Grid padded>
                          <Grid.Row>
                            <Grid.Column>
                              <p style={{ color: '#000000de' }}>Select the tag that you'd like to merge into:</p>
                              {mergeRecords.map((mergeRecord: any, mergeIndex: number) => {
                                return (
                                  <div className='merge-tag-header' style={{ paddingBottom: '10px' }}>
                                    <Form.Radio
                                      name={`record-${mergeIndex}`}
                                      id={`record${mergeIndex}`}
                                      label={`${mergeRecord.name} (${mergeRecord.caseCount} cases)`}
                                      value={mergeRecord.name}
                                      checked={newTagName === mergeRecord.name}
                                      onChange={(e, data) => handleRadioButtonChange(e, data, mergeRecord.name)}
                                      style={{ margin: '0rem', fontSize: '1.4rem' }}
                                    />
                                  </div>
                                );
                              })}
                              <p style={{ color: '#000000de', marginTop: '1.2rem' }}>All cases (if applicable) will be updated to use the selected tag.</p>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Dimmer.Dimmable>
                    </Modal.Content>
                    <Modal.Actions>
                      <Grid>
                        <Grid.Column>
                          <Button type="submit" onClick={handleMergeTags} className='violet right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem' }}>Merge</Button>
                          <Button type="button" className='second right floated button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white' }} onClick={closeModal}>Cancel</Button>
                        </Grid.Column>
                      </Grid>
                    </Modal.Actions>
                  </Modal>
                </>
              )}
              <DataGrid<TagViewModel>
                apiUrl={`/AdminSetting/TagDataGrid`}
                initialViewModelURL={`/AdminSetting/GetTagDataGrid`}
                columns={columns}
                rowsPerPage={10}
                defaultSortByField={'name'}
                defaultSortOrderDesc={false}
                rowKey={(row: TagViewModel) => row.tagId}
                noDataMessage={'No tag(s) to display'}
                searchPlaceholder='Search by tags'
                filters={filters}
                setFilters={setFilters}
                responseDataModel={responseDataModel}
                setResponseDataModel={setResponseDataModel}
                wardResponse={wardResponse}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </section>
  );
}

export default Tags;